import { useState, FormEvent } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    if (formData.message.length > 500) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      const formElement = e.currentTarget;
      const formDataToSend = new URLSearchParams();
      const formElements = formElement.elements;
      for (let i = 0; i < formElements.length; i++) {
        const element = formElements[i] as HTMLInputElement | HTMLTextAreaElement;
        if (element.name) {
          formDataToSend.append(element.name, element.value);
        }
      }

      const response = await fetch('https://readdy.ai/api/form/d6jmvavrgrhbthj8n1p0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formDataToSend.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', company: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="pt-16 md:pt-20">
        <div className="flex flex-col md:grid md:grid-cols-2 min-h-[calc(100vh-64px)] md:min-h-[calc(100vh-80px)]">

          {/* Left – Contact Info */}
          <div className="bg-[#1A2B4A] px-8 sm:px-12 md:px-20 py-16 md:py-0 flex items-center">
            <div className="w-full">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 md:mb-8">
                Get In Touch
              </h1>
              <p className="text-base md:text-xl text-gray-300 mb-10 md:mb-12 leading-relaxed">
                Let's discuss how we can support your logistics operations
              </p>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-6 h-6 flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-map-pin-line text-2xl text-[#D97642]"></i>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Location</div>
                    <div className="text-white text-sm md:text-base">Darwin, Northern Territory</div>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-mail-line text-2xl text-[#D97642]"></i>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Email</div>
                    <div className="text-white text-sm md:text-base break-all">contact@clogtransport.com.au</div>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-phone-line text-2xl text-[#D97642]"></i>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Phone</div>
                    <div className="text-white text-sm md:text-base">0414 968 280</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right – Contact Form */}
          <div className="bg-white px-8 sm:px-12 md:px-20 py-16 md:py-0 flex items-center">
            <div className="w-full">
              <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6 md:space-y-8">
                <div>
                  <label htmlFor="name" className="block text-xs text-gray-600 uppercase tracking-wider mb-2 font-medium">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full border-0 border-b-2 border-gray-300 focus:border-[#1A2B4A] outline-none py-3 text-sm md:text-base text-[#2C2C2C] transition-colors bg-transparent"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-xs text-gray-600 uppercase tracking-wider mb-2 font-medium">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full border-0 border-b-2 border-gray-300 focus:border-[#1A2B4A] outline-none py-3 text-sm md:text-base text-[#2C2C2C] transition-colors bg-transparent"
                  />
                </div>
                <div>
                  <label htmlFor="company" className="block text-xs text-gray-600 uppercase tracking-wider mb-2 font-medium">
                    Company
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="w-full border-0 border-b-2 border-gray-300 focus:border-[#1A2B4A] outline-none py-3 text-sm md:text-base text-[#2C2C2C] transition-colors bg-transparent"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-xs text-gray-600 uppercase tracking-wider mb-2 font-medium">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full border-0 border-b-2 border-gray-300 focus:border-[#1A2B4A] outline-none py-3 text-sm md:text-base text-[#2C2C2C] resize-none transition-colors bg-transparent"
                  ></textarea>
                  <div className="text-xs text-gray-500 mt-1 text-right">
                    {formData.message.length}/500 characters
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#1A2B4A] text-white font-medium py-4 rounded-lg hover:bg-[#D97642] transition-colors duration-300 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                    Thank you for your message. We'll be in touch soon.
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                    There was an error sending your message. Please try again.
                  </div>
                )}
              </form>
            </div>
          </div>

        </div>
      </div>

      <Footer />
    </div>
  );
}
